import React from "react";
import { Link } from "react-router-dom";
import { Menu, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "@/components/landing/ThemeToggle";
import { LogoMark, LogoWordmark } from "@/components/landing/Logo";
import { useAuth } from "@/context/AuthContext";
import { appPath } from "@/lib/paths";
import { useT } from "@/i18n/I18nContext";
import { openDemoModal } from "@/components/landing/DemoModal";

const ANCHORS = [
  { href: "#functionalities", label: { ro: "Funcționalități", en: "Features" }, testid: "landing-nav-anchor-features" },
  { href: "#results", label: { ro: "Beneficii", en: "Benefits" }, testid: "landing-nav-anchor-benefits" },
  { href: "#industries", label: { ro: "Industrii", en: "Industries" }, testid: "landing-nav-anchor-industries" },
  { href: "#pricing", label: { ro: "Prețuri", en: "Pricing" }, testid: "landing-nav-anchor-pricing" },
  { href: "#resources", label: { ro: "Resurse", en: "Resources" }, testid: "landing-nav-anchor-resources" },
  { href: "#about", label: { ro: "Despre noi", en: "About" }, testid: "landing-nav-anchor-about" },
];

const navyBtn =
  "inline-flex h-10 items-center justify-center gap-1.5 rounded-lg bg-[hsl(var(--lp-navy))] px-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[hsl(var(--lp-navy-deep))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--lp-navy))] focus-visible:ring-offset-2";

export const LandingNav = () => {
  const { t, lang } = useT();
  const { user } = useAuth();
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const appHref = user ? (user.role === "client" ? "/portal" : appPath()) : null;

  return (
    <header
      className={`sticky top-0 z-50 transition-[background-color,box-shadow,border-color] duration-200 ${
        scrolled
          ? "border-b border-[hsl(var(--lp-border))] bg-[hsl(var(--lp-bg)/0.85)] shadow-sm backdrop-blur supports-[backdrop-filter]:bg-[hsl(var(--lp-bg)/0.7)]"
          : "border-b border-transparent bg-[hsl(var(--lp-bg)/0.4)]"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-2" data-testid="landing-nav-logo-link" aria-label="LactoPilot">
          <LogoMark className="h-9 w-9" />
          <LogoWordmark tagline={t("landing.nav.tagline")} />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {ANCHORS.map((a) => (
            <a
              key={a.href}
              href={a.href}
              data-testid={a.testid}
              className="rounded-md px-3 py-2 text-sm font-medium text-[hsl(var(--lp-body))] transition-colors hover:bg-[hsl(var(--lp-navy)/0.06)] hover:text-[hsl(var(--lp-ink))]"
            >
              {a.label[lang] || a.label.en}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-1.5">
          <div className="hidden sm:block">
            <LanguageSwitcher />
          </div>
          <ThemeToggle />

          {user ? (
            <Link to={appHref} className={`hidden sm:inline-flex ${navyBtn}`} data-testid="landing-nav-open-app-button">
              {t("landing.nav.openApp")} <ArrowRight className="h-4 w-4" />
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className="hidden rounded-md px-3 py-2 text-sm font-semibold text-[hsl(var(--lp-navy))] transition-colors hover:bg-[hsl(var(--lp-navy)/0.06)] sm:inline-flex"
                data-testid="landing-nav-login-button"
              >
                {t("landing.nav.login")}
              </Link>
              <button type="button" onClick={openDemoModal} className={`hidden sm:inline-flex ${navyBtn}`} data-testid="landing-nav-get-started-button">
                {lang === "ro" ? "Programează un demo" : "Schedule a demo"} <ArrowRight className="h-4 w-4" />
              </button>
            </>
          )}

          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" aria-label={t("landing.nav.menu")} data-testid="landing-nav-mobile-trigger">
                <Menu className="h-5 w-5 text-[hsl(var(--lp-ink))]" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px]">
              <div className="mt-2 flex flex-col gap-1">
                {ANCHORS.map((a) => (
                  <SheetClose asChild key={a.href}>
                    <a
                      href={a.href}
                      className="rounded-md px-3 py-2.5 text-sm font-medium text-[hsl(var(--lp-ink))] transition-colors hover:bg-[hsl(var(--lp-navy)/0.06)]"
                      data-testid={`${a.testid}-mobile`}
                    >
                      {a.label[lang] || a.label.en}
                    </a>
                  </SheetClose>
                ))}
                <div className="my-3 h-px bg-[hsl(var(--lp-border))]" />
                <div className="px-3">
                  <LanguageSwitcher />
                </div>
                <div className="mt-3 flex flex-col gap-2 px-1">
                  {user ? (
                    <SheetClose asChild>
                      <Link to={appHref} className={navyBtn} data-testid="landing-nav-open-app-button-mobile">
                        {t("landing.nav.openApp")}
                      </Link>
                    </SheetClose>
                  ) : (
                    <>
                      <SheetClose asChild>
                        <Link
                          to="/login"
                          className="inline-flex h-10 items-center justify-center rounded-lg border border-[hsl(var(--lp-navy)/0.3)] px-4 text-sm font-semibold text-[hsl(var(--lp-navy))]"
                          data-testid="landing-nav-login-button-mobile"
                        >
                          {t("landing.nav.login")}
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <button type="button" onClick={openDemoModal} className={navyBtn} data-testid="landing-nav-get-started-button-mobile">
                          {lang === "ro" ? "Programează un demo" : "Schedule a demo"}
                        </button>
                      </SheetClose>
                    </>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
